/**
 * ValidationPanel — Sprint 15
 *
 * Shown in the wizard after steps 3 and 4, displaying any revenue-validation
 * warnings produced by the POST /api/v1/analyses/validate endpoint.
 *
 * Each warning can be dismissed with a written justification ("bypass").
 * Bypasses are collected by the parent and included in the calculate payload.
 */

import type { ValidationBypass, ValidationWarning } from '@develio/shared';
import { useState } from 'react';

interface Props {
  warnings: ValidationWarning[];
  bypasses: ValidationBypass[];
  onBypassChange: (bypasses: ValidationBypass[]) => void;
}

const SEVERITY_STYLES = {
  warning: {
    container: 'border-red-200 bg-red-50',
    badge: 'bg-red-100 text-red-700',
    label: 'Warning',
  },
  caution: {
    container: 'border-amber-200 bg-amber-50',
    badge: 'bg-amber-100 text-amber-700',
    label: 'Caution',
  },
} as const;

function fmt(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

/**
 * Single collapsible warning card with optional bypass justification input.
 */
function WarningCard({
  warning,
  bypass,
  onBypass,
  onClearBypass,
}: {
  warning: ValidationWarning;
  bypass: ValidationBypass | undefined;
  onBypass: (justification: string) => void;
  onClearBypass: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [justification, setJustification] = useState(bypass?.justification ?? '');
  const styles = SEVERITY_STYLES[warning.severity];

  const handleSave = () => {
    if (justification.trim().length < 10) return;
    onBypass(justification.trim());
    setExpanded(false);
  };

  return (
    <div className={`rounded-lg border p-4 ${styles.container}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`rounded px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${styles.badge}`}
            >
              {styles.label}
            </span>
            <span className="text-xs text-slate-500">{warning.metric}</span>
          </div>
          <p className="text-sm text-slate-700">{warning.message}</p>
          <div className="mt-2 flex gap-4 text-xs text-slate-500">
            <span>P25: {fmt(warning.p25)}</span>
            <span>Median: {fmt(warning.p50)}</span>
            <span>P75: {fmt(warning.p75)}</span>
            <span>({warning.year})</span>
          </div>
        </div>

        <div className="flex-shrink-0">
          {bypass ? (
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                Acknowledged
              </span>
              <button
                type="button"
                onClick={onClearBypass}
                className="text-xs text-slate-400 hover:text-slate-600"
              >
                Undo
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setExpanded((x) => !x)}
              className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              {expanded ? 'Cancel' : 'Acknowledge'}
            </button>
          )}
        </div>
      </div>

      {expanded && !bypass && (
        <div className="mt-3 border-t border-slate-200 pt-3">
          <label
            htmlFor={`bypass-justification-${warning.field}`}
            className="block text-xs font-medium text-slate-600 mb-1"
          >
            Justification <span className="text-slate-400">(required, min 10 chars)</span>
          </label>
          <textarea
            id={`bypass-justification-${warning.field}`}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            rows={2}
            placeholder="Explain why this value is correct for this project…"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
          />
          <button
            type="button"
            onClick={handleSave}
            disabled={justification.trim().length < 10}
            className="mt-2 rounded-md bg-slate-800 px-4 py-1.5 text-xs font-medium text-white disabled:opacity-40 hover:bg-slate-700"
          >
            Save justification
          </button>
        </div>
      )}
    </div>
  );
}

export function ValidationPanel({ warnings, bypasses, onBypassChange }: Props) {
  if (warnings.length === 0) return null;

  const warningCount = warnings.filter((w) => w.severity === 'warning').length;
  const cautionCount = warnings.filter((w) => w.severity === 'caution').length;
  const bypassedCount = bypasses.length;
  const pendingCount = warnings.length - bypassedCount;

  const handleBypass = (warning: ValidationWarning, justification: string) => {
    const existing = bypasses.filter((b) => b.field !== warning.field);
    onBypassChange([
      ...existing,
      {
        field: warning.field,
        metric: warning.metric,
        analystValue: warning.analystValue,
        derivedValue: warning.derivedValue,
        derivedLabel: warning.derivedLabel,
        severity: warning.severity,
        justification,
      },
    ]);
  };

  const handleClearBypass = (warning: ValidationWarning) => {
    onBypassChange(bypasses.filter((b) => b.field !== warning.field));
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Input Validation</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Inputs compared against industry benchmarks — review before running analysis
          </p>
        </div>
        <div className="flex gap-2 text-xs">
          {warningCount > 0 && (
            <span className="rounded-full bg-red-100 px-2.5 py-0.5 font-medium text-red-700">
              {warningCount} warning{warningCount !== 1 ? 's' : ''}
            </span>
          )}
          {cautionCount > 0 && (
            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 font-medium text-amber-700">
              {cautionCount} caution{cautionCount !== 1 ? 's' : ''}
            </span>
          )}
          {pendingCount === 0 && (
            <span className="rounded-full bg-green-100 px-2.5 py-0.5 font-medium text-green-700">
              All acknowledged
            </span>
          )}
        </div>
      </div>

      {/* Warning cards */}
      <div className="space-y-3">
        {warnings.map((w) => (
          <WarningCard
            key={w.field}
            warning={w}
            bypass={bypasses.find((b) => b.field === w.field)}
            onBypass={(justification) => handleBypass(w, justification)}
            onClearBypass={() => handleClearBypass(w)}
          />
        ))}
      </div>

      {pendingCount > 0 && (
        <p className="mt-4 text-xs text-slate-500">
          You can run the analysis without acknowledging these flags. Unacknowledged warnings will
          appear in the validation report.
        </p>
      )}
    </div>
  );
}
